// src/components/Schedule/PickupsCalendar.js

import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ScheduleDayPanel from "./ScheduleDayPanel";
import "../../style/Schedule.css";
import "../../style/ScheduleDayPanel.css";

function getDaysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function parseYMD(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const s = ymd.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;

  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));

  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (m < 0 || m > 11 || d < 1 || d > 31) return null;

  return { y, m, d };
}

function PickupsCalendar({
  darkMode = false,
  events = [],
  onScheduleRow,
  onConfirmRow,
}) {
  const { t, i18n } = useTranslation();
  const rootRef = useRef(null);

  const [calView, setCalView] = useState("month");
  const [selectedDay, setSelectedDay] = useState(null);
  const [closing, setClosing] = useState(false);
  const closingTimerRef = useRef(null);

  // stash panel data so it stays visible during exit animation
  const lastPanelRef = useRef({ day: null, events: [] });

  // cleanup closing timer on unmount
  useEffect(() => () => clearTimeout(closingTimerRef.current), []);

  const monthNames = useMemo(() => {
    try {
      const fmt = new Intl.DateTimeFormat(i18n.language, { month: "long" });
      return Array.from({ length: 12 }, (_, m) =>
        fmt.format(new Date(2020, m, 1))
      );
    } catch {
      return [
        "January","February","March","April","May","June",
        "July","August","September","October","November","December",
      ];
    }
  }, [i18n.language]);

  const monthNamesShort = useMemo(() => {
    try {
      const fmt = new Intl.DateTimeFormat(i18n.language, { month: "short" });
      return Array.from({ length: 12 }, (_, m) =>
        fmt.format(new Date(2020, m, 1))
      );
    } catch {
      return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    }
  }, [i18n.language]);

  const weekdayLabels = useMemo(() => {
    try {
      const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: "short" });
      return Array.from({ length: 7 }, (_, i) =>
        fmt.format(new Date(2020, 0, 5 + i)).toUpperCase()
      );
    } catch {
      return ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    }
  }, [i18n.language]);

  const weekdayInitials = useMemo(() => {
    try {
      const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: "narrow" });
      return Array.from({ length: 7 }, (_, i) =>
        fmt.format(new Date(2020, 0, 5 + i))
      );
    } catch {
      return ["S", "M", "T", "W", "T", "F", "S"];
    }
  }, [i18n.language]);

  const parsedEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return events
      .map((ev) => {
        const p = parseYMD(ev?.date);
        if (!p) return null;

        const rawTitle = ev?.title;
        const safeTitle =
          typeof rawTitle === "string" && rawTitle.trim() ? rawTitle : "—";

        return {
          id: String(ev?.id ?? `${p.y}-${p.m}-${p.d}-${safeTitle}`),
          y: p.y,
          m: p.m,
          d: p.d,
          title: safeTitle,
          material: ev?.material || "—",
          donor: ev?.donor || "—",
          timeWindow: ev?.timeWindow || "—",
          timeStart: ev?.timeStart || null,
          timeEnd: ev?.timeEnd || null,
          requestedQty: ev?.requestedQty,
          allocatedQty: ev?.allocatedQty,
          status: ev?.status || "scheduled",
        };
      })
      .filter(Boolean);
  }, [events]);

  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());

  const now = new Date();
  const isViewingCurrentMonth = currentYear === now.getFullYear() && currentMonth === now.getMonth();

  const goToPrev = () => {
    if (calView === "year") { setCurrentYear((y) => y - 1); return; }
    setCurrentMonth((m) => {
      if (m === 0) { setCurrentYear((y) => y - 1); return 11; }
      return m - 1;
    });
  };

  const goToNext = () => {
    if (calView === "year") { setCurrentYear((y) => y + 1); return; }
    setCurrentMonth((m) => {
      if (m === 11) { setCurrentYear((y) => y + 1); return 0; }
      return m + 1;
    });
  };

  const goToToday = () => {
    const n = new Date();
    setCurrentMonth(n.getMonth());
    setCurrentYear(n.getFullYear());
    setCalView("month");
  };

  const handleMiniMonthClick = useCallback((monthIdx) => {
    setCurrentMonth(monthIdx);
    setCalView("month");
    setSelectedDay(null);
    setClosing(false);
    clearTimeout(closingTimerRef.current);
  }, []);

  // animated close — panel slides out, then unmounts
  const closePanel = useCallback(() => {
    if (closing || selectedDay === null) return;
    setClosing(true);
    closingTimerRef.current = setTimeout(() => {
      setSelectedDay(null);
      setClosing(false);
    }, 550);
  }, [closing, selectedDay]);

  const handleDayClick = useCallback((dayNumber) => {
    setSelectedDay((prev) => {
      if (prev === dayNumber) {
        // toggle off — use animated close
        closePanel();
        return prev; // don't clear yet, closePanel handles it
      }
      // switching to a different day — cancel any in-progress close
      setClosing(false);
      clearTimeout(closingTimerRef.current);
      return dayNumber;
    });
  }, [closePanel]);

  const eventsForMonth = useMemo(() => {
    return parsedEvents.filter((e) => e.y === currentYear && e.m === currentMonth);
  }, [parsedEvents, currentYear, currentMonth]);

  const pickupsCount = eventsForMonth.length;

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const e of eventsForMonth) {
      const list = map.get(e.d) || [];
      list.push(e);
      map.set(e.d, list);
    }
    return map;
  }, [eventsForMonth]);

  // events for selected day (passed to panel)
  const selectedDayEvents = useMemo(() => {
    if (selectedDay === null) return [];
    return eventsByDay.get(selectedDay) || [];
  }, [selectedDay, eventsByDay]);

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
    const todayDay = now.getDate();
    const isCurrentMonth = currentYear === now.getFullYear() && currentMonth === now.getMonth();

    const cells = [];

    for (let i = 0; i < firstDayOfWeek; i += 1) {
      cells.push({ isEmpty: true, key: `empty-${i}` });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push({
        isEmpty: false,
        key: `day-${day}`,
        dayNumber: day,
        events: eventsByDay.get(day) || [],
        isToday: isCurrentMonth && day === todayDay,
      });
    }

    const remainder = cells.length % 7;
    if (remainder !== 0) {
      for (let i = 0; i < 7 - remainder; i += 1) {
        cells.push({ isEmpty: true, key: `trail-${i}` });
      }
    }

    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear, currentMonth, eventsByDay]);

  // year view: events per month for dot indicators
  const yearEventsByMonth = useMemo(() => {
    const map = new Map();
    for (const e of parsedEvents) {
      if (e.y !== currentYear) continue;
      const list = map.get(e.m) || [];
      list.push(e);
      map.set(e.m, list);
    }
    return map;
  }, [parsedEvents, currentYear]);

  // keyboard nav: ← → to change day, Escape to close panel
  useEffect(() => {
    if (selectedDay === null) return;

    const handleKey = (e) => {
      if (e.key === "Escape") {
        closePanel();
        return;
      }

      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();

      const delta = e.key === "ArrowLeft" ? -1 : 1;
      setSelectedDay((prev) => {
        const dim = getDaysInMonth(currentYear, currentMonth);
        const next = (prev || 1) + delta;

        if (next < 1) {
          const pm = currentMonth === 0 ? 11 : currentMonth - 1;
          const py = currentMonth === 0 ? currentYear - 1 : currentYear;
          setCurrentMonth(pm);
          if (py !== currentYear) setCurrentYear(py);
          return getDaysInMonth(py, pm);
        }

        if (next > dim) {
          const nm = currentMonth === 11 ? 0 : currentMonth + 1;
          const ny = currentMonth === 11 ? currentYear + 1 : currentYear;
          setCurrentMonth(nm);
          if (ny !== currentYear) setCurrentYear(ny);
          return 1;
        }

        return next;
      });
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [selectedDay, currentMonth, currentYear, closePanel]);

  // ---- year view ----
  const renderYearView = () => {
    const todayM = now.getMonth();
    const todayD = now.getDate();
    const isThisYear = currentYear === now.getFullYear();

    return (
      <Box className="cal-year-grid">
        {Array.from({ length: 12 }, (_, monthIdx) => {
          const daysInMonth = getDaysInMonth(currentYear, monthIdx);
          const firstDow = new Date(currentYear, monthIdx, 1).getDay();
          const monthEvents = yearEventsByMonth.get(monthIdx) || [];
          const eventDays = new Map();
          for (const e of monthEvents) {
            if (!eventDays.has(e.d)) eventDays.set(e.d, []);
            eventDays.get(e.d).push(e.status);
          }
          const isCurrentMini = isThisYear && monthIdx === todayM;

          return (
            <Box
              key={`ym-${monthIdx}`}
              className={`cal-mini-month${isCurrentMini ? " cal-mini-month--current" : ""}`}
              onClick={() => handleMiniMonthClick(monthIdx)}
            >
              <Typography className="cal-mini-month__title">
                {monthNamesShort[monthIdx]}
                {monthEvents.length > 0 && (
                  <span className="cal-mini-month__count">{monthEvents.length}</span>
                )}
              </Typography>
              <Box className="cal-mini-grid">
                {weekdayInitials.map((lbl, i) => (
                  <span key={`wdi-${monthIdx}-${i}`} className="cal-mini-wday">{lbl}</span>
                ))}
                {Array.from({ length: firstDow }, (_, i) => (
                  <span key={`me-${monthIdx}-${i}`} className="cal-mini-empty" />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const d = i + 1;
                  const statuses = eventDays.get(d);
                  const isMiniToday = isCurrentMini && d === todayD;
                  const dotStatus = statuses
                    ? (statuses.includes("late") ? "late"
                      : statuses.includes("pending") ? "pending"
                      : statuses.includes("scheduled") ? "scheduled"
                      : "delivered")
                    : null;

                  return (
                    <span
                      key={`md-${monthIdx}-${d}`}
                      className={`cal-mini-day${isMiniToday ? " cal-mini-day--today" : ""}${dotStatus ? ` cal-mini-day--${dotStatus}` : ""}`}
                    >
                      {d}
                    </span>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const isPanelOpen = selectedDay !== null && selectedDayEvents.length > 0;

  // stash panel data when open so it persists during exit animation
  if (isPanelOpen) {
    lastPanelRef.current = { day: selectedDay, events: selectedDayEvents };
  }

  const showPanel = isPanelOpen || closing;
  const panelDay = isPanelOpen ? selectedDay : lastPanelRef.current.day;
  const panelEvents = isPanelOpen ? selectedDayEvents : lastPanelRef.current.events;

  return (
    <Box
      ref={rootRef}
      className={`schedule-calendar-root ${darkMode ? "dark" : "light"}`}
      tabIndex={-1}
    >
      <Box className="schedule-calendar-header">
        <Box className="schedule-calendar-nav">
          <button className="schedule-calendar-nav-btn" onClick={goToPrev} aria-label="Previous">
            ‹
          </button>
          <Typography variant="subtitle1" className={`schedule-calendar-title${isViewingCurrentMonth && calView === "month" ? " schedule-calendar-title--current" : ""}`}>
            {calView === "year" ? currentYear : `${monthNames[currentMonth]} ${currentYear}`}
          </Typography>
          <button className="schedule-calendar-nav-btn" onClick={goToNext} aria-label="Next">
            ›
          </button>
          <button className="schedule-calendar-today-btn" onClick={goToToday}>
            {t("schedule.calendar.today", "Today")}
          </button>
        </Box>

        <Box className="schedule-calendar-header-right">
          <div className="cal-view-toggle">
            <button
              className={`cal-view-toggle__btn${calView === "month" ? " active" : ""}`}
              onClick={() => { setCalView("month"); setSelectedDay(null); setClosing(false); clearTimeout(closingTimerRef.current); }}
            >
              {t("schedule.calendar.month", "Month")}
            </button>
            <button
              className={`cal-view-toggle__btn${calView === "year" ? " active" : ""}`}
              onClick={() => { setCalView("year"); setSelectedDay(null); setClosing(false); clearTimeout(closingTimerRef.current); }}
            >
              {t("schedule.calendar.year", "Year")}
            </button>
          </div>

          {calView === "month" && (
            <Typography variant="body2" className="schedule-calendar-count">
              {pickupsCount} {t("pickups", { defaultValue: "pickups" })}
            </Typography>
          )}
        </Box>
      </Box>

      {calView === "month" && (
        <div className="cal-with-panel">
          <div className="cal-main">
            <Box className="schedule-weekdays">
              {weekdayLabels.map((label, i) => (
                <Box key={`${label}-${i}`} className="schedule-weekday-label">{label}</Box>
              ))}
            </Box>

            <Box className="calendar-grid">
              {calendarDays.map((day) => {
                if (day.isEmpty) return <Box key={day.key} className="calendar-day-empty" />;

                const isSelected = selectedDay === day.dayNumber;

                return (
                  <Box
                    key={day.key}
                    className={`calendar-day-cell${day.isToday ? " calendar-day--today" : ""}${isSelected ? " calendar-day--selected" : ""}`}
                    onClick={() => day.events.length > 0 && handleDayClick(day.dayNumber)}
                    style={{ cursor: day.events.length > 0 ? "pointer" : "default" }}
                  >
                    <Typography variant="caption" className="calendar-day-number">
                      {day.dayNumber}
                    </Typography>

                    <Box className="calendar-day-events">
                      {day.events.map((event) => (
                        <Typography
                          key={`${day.key}-${event.requestId || event.id}`}
                          variant="body2"
                          className={`calendar-day-event cal-event--${event.status}`}
                          title={event.title}
                        >
                          {event.title}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </div>

          {showPanel && (
            <ScheduleDayPanel
              year={currentYear}
              month={currentMonth}
              day={panelDay}
              events={panelEvents}
              darkMode={darkMode}
              closing={closing}
              onClose={closePanel}
              onScheduleRow={onScheduleRow}
              onConfirmRow={onConfirmRow}
            />
          )}
        </div>
      )}

      {calView === "year" && renderYearView()}
    </Box>
  );
}

export default PickupsCalendar;
